import { useLayoutEffect, useState } from "react";

/**
 * `ProInteractionA` 의 스크롤 캔버스가 활성되는 뷰포트 조건 — **단일 출처**.
 *
 * 이 판정은 두 곳에서 쓰이고, **반드시 같은 답이어야 한다**:
 *
 *   1. `pro-interaction-a.tsx` — 참이면 100vh 캔버스를 그리고, 거짓이면 대체 영상을 보여준다.
 *   2. 캔버스 **다음** 섹션(`ProductFeatureSections` 의 Pro 분기) — 참일 때만 `-mt-[100vh]` 로
 *      캔버스 위에 겹쳐 올린다.
 *
 * 둘이 어긋나면 화면이 조용히 깨진다 — 캔버스 없이 `-mt-[100vh]` 가 걸리면 다음 섹션이
 * 이전 섹션을 덮고, 반대면 빈 화면 한 폭이 남는다. 기본 창 크기에서는 잘 안 보이는 버그라
 * 상수를 복사하지 말고 이 모듈을 쓸 것.
 *
 * (원래 `/tactsuit/tactsuit-pro` 페이지 안에만 있던 조건인데, #5068 통합으로 PDP 공용
 *  컴포넌트로 옮겨지면서 두 벌이 됐다.)
 */

/** 이 비율보다 납작해야(가로가 넉넉해야) 캔버스를 켠다. */
const MIN_ASPECT_RATIO = 1.4;
/** 초광폭 모니터에서는 프레임 이미지가 감당이 안 돼 캔버스를 끈다. */
const MAX_CLIENT_WIDTH = 2400;

/** 지금 뷰포트가 스크롤 캔버스를 켤 조건인가. 브라우저에서만 호출할 것(SSR 에서는 false). */
export function isProInteractionViewport(): boolean {
  if (typeof window === "undefined") return false;
  const ratio = window.innerWidth / window.innerHeight;
  return (
    ratio >= MIN_ASPECT_RATIO &&
    document.documentElement.clientWidth <= MAX_CLIENT_WIDTH
  );
}

/**
 * 위 판정을 리렌더가 필요한 쪽(겹침 마진을 주는 상위 섹션)에서 쓰기 위한 훅.
 * 캔버스 자체는 리렌더 없이 DOM 을 직접 만지므로 `isProInteractionViewport()` 를 그대로 쓴다.
 */
export function useProInteractionViewport(): boolean {
  // SSR/최초 렌더는 false — 서버는 뷰포트를 모른다. 겹침 마진이 늦게 붙는 쪽이 안전하다
  // (없으면 여백, 잘못 붙으면 콘텐츠가 가려짐).
  const [active, setActive] = useState(false);

  useLayoutEffect(() => {
    const onResize = () => setActive(isProInteractionViewport());
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return active;
}
